import React from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { fetchQuestionnaire, submitQuestionnaire } from "../../../../../store/actions/QuestionnaireActions"

// Normalize whitespace for consistent string comparison
// Handles multiple spaces, leading/trailing spaces, etc.
const normalizeWhitespace = (str) => {
  if (!str) return ""
  return str.trim().replace(/\s+/g, " ")
}

// Check if a value matches an option name (case-insensitive, whitespace-normalized)
const valueMatchesOption = (value, optionName) => {
  if (!value || !optionName) return false
  return normalizeWhitespace(value.toLowerCase()) === normalizeWhitespace(optionName.toLowerCase())
}

// Generate unique ID for option questions to prevent duplicates
const generateUniqueOptionQuestionId = (questionId, option, optionQuestion, optionIndex) => {
  const optionId = option.option_id || option.id || optionIndex;
  const questionIdForUnique = optionQuestion.id || optionQuestion.form_question_id || optionIndex;
  return `${questionId}_${optionId}_${questionIdForUnique}`;
};

// Get all nested option question IDs from questions
const getNestedOptionQuestionIds = (questions) => {
  const nestedIds = new Set();

  const processQuestion = (question) => {
    if (question.options && Array.isArray(question.options)) {
      question.options.forEach((option) => {
        if (option.option_questions && Array.isArray(option.option_questions)) {
          option.option_questions.forEach((optionQuestion) => {
            nestedIds.add(optionQuestion.form_question_id);
            // Also process nested option questions recursively
            processQuestion(optionQuestion);
          });
        }
      });
    }
  };

  questions.forEach(processQuestion);
  return nestedIds;
};

const QuestionnaireComponent = ({ leadId, formId, onSubmit, isConverted, onUnsavedChangesChange }) => {
  const dispatch = useDispatch()
  const { fields, answers, loading, error, submitting } = useSelector((state) => state.questionnaireState)
  const [formAnswers, setFormAnswers] = React.useState(answers || {})
  const [filePreviews, setFilePreviews] = React.useState({})
  const [uploadedFiles, setUploadedFiles] = React.useState({})

  // Add state to track form validation errors
  const [formErrors, setFormErrors] = React.useState({})

  // Add state to track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)
  const [initialAnswers, setInitialAnswers] = React.useState({})
  const [visibleOptionQuestions, setVisibleOptionQuestions] = React.useState({})

  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const [submissionComplete, setSubmissionComplete] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  leadId = leadId || (fields ? fields.lead_id : null)

  // Get all nested option question IDs to filter from main group render
  const getAllSubquestionIds = React.useCallback(() => {
    const nestedIds = new Set();

    const processQuestion = (question, depth = 0) => {
      const indent = "  ".repeat(depth);
      if (question.options && Array.isArray(question.options)) {
        question.options.forEach((option) => {
          if (option.option_questions && Array.isArray(option.option_questions)) {
            console.log(`${indent}[v0] Found option_questions under Q${question.form_question_id} option "${option.option_name}": ${option.option_questions.length} nested`);
            option.option_questions.forEach((optionQuestion) => {
              nestedIds.add(optionQuestion.form_question_id);
              console.log(`${indent}  [v0] Added nested Q${optionQuestion.form_question_id}: "${optionQuestion.label}"`);
              // Also process nested option questions recursively
              processQuestion(optionQuestion, depth + 1);
            });
          }
        });
      }
    };

    if (fields && Array.isArray(fields)) {
      console.log(`[v0] ===== SCANNING FIELDS FOR NESTED QUESTIONS =====`);
      fields.forEach((field) => {
        if (field.isGroup === "Yes" && field.questions) {
          console.log(`[v0] GROUP: "${field.label}" (${field.questions.length} questions)`);
          field.questions.forEach((q) => {
            console.log(`[v0]   - Q${q.form_question_id}: "${q.label}" (${q.options?.length || 0} options)`);
            processQuestion(q);
          });
        } else {
          console.log(`[v0] STANDALONE Q: "${field.label}" (${field.options?.length || 0} options)`);
          processQuestion(field);
        }
      });
    }

    console.log(`[v0] ===== RESULT: ${nestedIds.size} NESTED QUESTION IDs FOUND =====`, Array.from(nestedIds));
    return nestedIds;
  }, [fields])

  // Debug logging
  console.log("QuestionnaireComponent - fields:", fields)
  console.log("QuestionnaireComponent - fields length:", fields?.length)

  // Count questions by type
  if (fields && fields.length > 0) {
    const questionTypes = {}
    fields.forEach((field) => {
      if (field.isGroup === "Yes" && field.questions) {
        field.questions.forEach((q) => {
          questionTypes[q.question_type] = (questionTypes[q.question_type] || 0) + 1
        })
      } else {
        questionTypes[field.question_type] = (questionTypes[field.question_type] || 0) + 1
      }
    })
    console.log("Question types count:", questionTypes)
  }

  React.useEffect(() => {
    if (leadId) {
      console.log("Fetching questionnaire for leadId:", leadId)
      dispatch(fetchQuestionnaire(leadId))
    }
  }, [dispatch, leadId])

  React.useEffect(() => {
    if (answers) {
      setFormAnswers((prev) => ({
        ...prev,
        ...answers,
      }))
      // Set initial answers for comparison
      setInitialAnswers(answers)
    }
  }, [answers])

  React.useEffect(() => {
    if (fields && fields.length > 0) {
      const answersFromAPI = {}
      let hasExistingAnswers = false

      const processOptionQuestions = (parentQuestionId, parentOption, optionQuestions) => {
        if (!optionQuestions || optionQuestions.length === 0) return

        optionQuestions.forEach((optionQuestion, optionIdx) => {
          if (optionQuestion.answer && optionQuestion.answer.trim() !== "") {
            const uniqueOptionQuestionId = generateUniqueOptionQuestionId(
              parentQuestionId,
              parentOption,
              optionQuestion,
              optionIdx
            )

            if (optionQuestion.question_type === "Multiple Selection" && optionQuestion.answer.includes(",")) {
              // Split by comma, normalize whitespace, and remove duplicates
              const answersArray = optionQuestion.answer
                .split(",")
                .map((item) => normalizeWhitespace(item))
                .filter((value, index, array) => array.indexOf(value) === index && value !== "")
              answersFromAPI[uniqueOptionQuestionId] = answersArray
              console.log("[v0] Multiple Selection option question loaded:")
              console.log("  - Question ID:", optionQuestion.form_question_id)
              console.log("  - Unique ID Key:", uniqueOptionQuestionId)
              console.log("  - Parent Question:", parentQuestionId)
              console.log("  - Parent Option:", parentOption)
              console.log("  - Normalized/Dedup Answers:", answersArray)
            } else if (optionQuestion.question_type === "File Input") {
              try {
                const fileData = JSON.parse(optionQuestion.answer)
                answersFromAPI[uniqueOptionQuestionId] = fileData
                console.log("[v0] File loaded from API (JSON) for option question:", fileData.fileName)
              } catch (e) {
                if (optionQuestion.answer.startsWith("data:")) {
                  const fileData = {
                    fileBase64: optionQuestion.answer,
                    fileName: `file_${uniqueOptionQuestionId}`,
                    fileType: optionQuestion.answer.split(";")[0].replace("data:", ""),
                  }
                  answersFromAPI[uniqueOptionQuestionId] = fileData
                  console.log("[v0] File loaded from API (base64) for option question:", fileData.fileName)
                } else {
                  const fileUrl = optionQuestion.answer
                  const fileData = {
                    fileBase64: fileUrl,
                    fileName: fileUrl.split("/").pop(),
                    fileType: fileUrl.match(/\.(jpg|jpeg|png|gif)$/i)
                      ? "image/png"
                      : fileUrl.match(/\.pdf$/i)
                      ? "application/pdf"
                      : "application/octet-stream",
                  }
                  answersFromAPI[uniqueOptionQuestionId] = fileData
                  console.log("[v0] File loaded from API (URL) for option question:", fileData)
                }
              }
            } else {
              // Normalize whitespace for all text answers
              answersFromAPI[uniqueOptionQuestionId] = normalizeWhitespace(optionQuestion.answer)
              console.log("[v0] Option question answer loaded:", uniqueOptionQuestionId, "=", normalizeWhitespace(optionQuestion.answer))
            }
            hasExistingAnswers = true
          }
        })
      }

      fields.forEach((field) => {
        if (field.isGroup === "Yes" && field.questions) {
          // Handle grouped questions
          field.questions.forEach((question) => {
            if (question.answer && question.answer.trim() !== "") {
              if (question.question_type === "Multiple Selection" && question.answer.includes(",")) {
                // Split by comma, normalize whitespace, and remove duplicates
                const answersArray = question.answer
                  .split(",")
                  .map((item) => normalizeWhitespace(item))
                  .filter((value, index, array) => array.indexOf(value) === index && value !== "")
                answersFromAPI[question.form_question_id] = answersArray
                console.log("[v0] Multiple Selection question - normalized/deduplicated answers:", question.form_question_id, "=", answersArray)
              } else if (question.question_type === "File Input") {
                // Handle file answers from API
                try {
                  const fileData = JSON.parse(question.answer)
                  answersFromAPI[question.form_question_id] = fileData
                  console.log("[v0] File loaded from API (JSON):", fileData.fileName)
                } catch (e) {
                  if (question.answer.startsWith("data:")) {
                    const fileData = {
                      fileBase64: question.answer,
                      fileName: `file_${question.form_question_id}`,
                      fileType: question.answer.split(";")[0].replace("data:", ""),
                    }
                    answersFromAPI[question.form_question_id] = fileData
                    console.log("[v0] File loaded from API (base64):", fileData.fileName, fileData.fileType)
                  } else {
                    const fileUrl = question.answer;
                    const fileData = {
                      fileBase64: fileUrl,
                      fileName: fileUrl.split("/").pop(),
                      fileType: fileUrl.match(/\.(jpg|jpeg|png|gif)$/i)
                        ? "image/png"
                        : fileUrl.match(/\.pdf$/i)
                          ? "application/pdf"
                          : "application/octet-stream",
                    };
                    answersFromAPI[question.form_question_id] = fileData;
                    console.log("[v0] File loaded from API (URL):", fileData);
                  }
                }
              } else {
                // Normalize whitespace for all text answers
                answersFromAPI[question.form_question_id] = normalizeWhitespace(question.answer)
              }
              hasExistingAnswers = true
            }

            // Load nested option questions answers from groups
            if (question.options && Array.isArray(question.options)) {
              question.options.forEach((option) => {
                processOptionQuestions(question.form_question_id, option, option.option_questions)
              })
            }
          })
        } else if (field.answer && field.answer.trim() !== "") {
          // Handle individual questions
          if (field.question_type === "Multiple Selection" && field.answer.includes(",")) {
            // Split by comma, normalize whitespace, and remove duplicates
            const answersArray = field.answer
              .split(",")
              .map((item) => normalizeWhitespace(item))
              .filter((value, index, array) => array.indexOf(value) === index && value !== "")
            answersFromAPI[field.form_question_id] = answersArray
            console.log("[v0] Standalone Multiple Selection - normalized/deduplicated answers:", field.form_question_id, "=", answersArray)
          } else if (field.question_type === "File Input") {
            try {
              const fileData = JSON.parse(field.answer)
              answersFromAPI[field.form_question_id] = fileData
              console.log("[v0] File loaded from API (JSON):", fileData.fileName)
            } catch (e) {
              if (field.answer.startsWith("data:")) {
                const fileData = {
                  fileBase64: field.answer,
                  fileName: `file_${field.form_question_id}`,
                  fileType: field.answer.split(";")[0].replace("data:", ""),
                }
                answersFromAPI[field.form_question_id] = fileData
                console.log("[v0] File loaded from API (base64):", fileData.fileName, fileData.fileType)
              } else {
                const fileUrl = field.answer;
                const fileData = {
                  fileBase64: fileUrl,
                  fileName: fileUrl.split("/").pop(),
                  fileType: fileUrl.match(/\.(jpg|jpeg|png|gif)$/i)
                    ? "image/png"
                    : fileUrl.match(/\.pdf$/i)
                    ? "application/pdf"
                    : "application/octet-stream",
                };
                answersFromAPI[field.form_question_id] = fileData;
                console.log("[v0] File loaded from API (URL):", fileData);
              }
            }
          } else {
            // Normalize whitespace for all text answers
            answersFromAPI[field.form_question_id] = normalizeWhitespace(field.answer)
          }
          hasExistingAnswers = true

          // Load nested option questions answers for standalone questions
          if (field.options && Array.isArray(field.options)) {
            field.options.forEach((option) => {
              processOptionQuestions(field.form_question_id, option, option.option_questions)
            })
          }
        }
      })

      console.log("[v0] Answers from API (including option questions):", answersFromAPI)
// ===== AUTO‑SELECT PARENT OPTIONS IF NESTED ANSWERS EXIST =====
// ===== AUTO‑SELECT PARENT OPTIONS IF NESTED ANSWERS EXIST (IMPROVED) =====
fields.forEach((field) => {
  const processParentSelection = (question) => {
    if (!question.options || question.options.length === 0) return;
    question.options.forEach((option) => {
      if (option.option_questions && option.option_questions.length > 0) {
        let hasNestedAnswer = false;
        option.option_questions.forEach((optionQuestion, idx) => {
          const nestedKey = generateUniqueOptionQuestionId(
            question.form_question_id,
            option,
            optionQuestion,
            idx
          );
          if (answersFromAPI[nestedKey]) {
            hasNestedAnswer = true;
            console.log(`[v0] Nested answer found for parent Q${question.form_question_id} option "${option.option_name}"`);
          }
        });
        if (hasNestedAnswer) {
          const parentKey = question.form_question_id;
          const currentParentVal = answersFromAPI[parentKey];
          const optionName = option.option_name.trim();
          if (question.question_type === "Multiple Selection" || question.question_type === "Checkbox") {
            let existing = Array.isArray(currentParentVal) ? currentParentVal : [];
            existing = existing.map(v => (v || "").trim());
            if (!existing.includes(optionName)) {
              existing.push(optionName);
              answersFromAPI[parentKey] = existing;
              console.log(`[v0] Auto‑selected parent option "${optionName}" for Q${parentKey}`);
            }
          } else {
            const trimmedCurrent = (currentParentVal || "").trim();
            if (trimmedCurrent !== optionName) {
              answersFromAPI[parentKey] = optionName;
              console.log(`[v0] Auto‑selected parent option "${optionName}" for Q${parentKey}`);
            }
          }
        }
      }
    });
  };

  if (field.isGroup === "Yes" && field.questions) {
    field.questions.forEach(processParentSelection);
  } else {
    processParentSelection(field);
  }
});
// ===== END OF AUTO‑SELECT BLOCK =====
// ===== END OF AUTO‑SELECT BLOCK =====
      if (Object.keys(answersFromAPI).length > 0) {
        setFormAnswers((prev) => ({
          ...prev,
          ...answersFromAPI,
        }))
        setInitialAnswers(answersFromAPI)

        if (hasExistingAnswers) {
          setIsSubmitted(true)
          setSubmissionComplete(true)
        }
      }
    }
  }, [fields])
  React.useEffect(() => {
    // ✅ prevent modal flash while submitting
    if (isSubmitting || submissionComplete || isConverted) {
      setHasUnsavedChanges(false)
  
      if (onUnsavedChangesChange) {
        onUnsavedChangesChange(false)
      }
  
      return
    }
  
    const hasAnyAnswers = Object.keys(formAnswers).some((key) => {
      const value = formAnswers[key]
  
      return (
        value &&
        value !== "" &&
        (!Array.isArray(value) || value.length > 0)
      )
    })
  
    const hasChanges =
      JSON.stringify(formAnswers) !== JSON.stringify(initialAnswers)
  
    const shouldShowUnsaved =
      hasAnyAnswers && hasChanges && !submissionComplete
  
    console.log("Unsaved changes check:", {
      hasAnyAnswers,
      hasChanges,
      submissionComplete,
      isSubmitting,
      shouldShowUnsaved,
    })
  
    setHasUnsavedChanges(shouldShowUnsaved)
  
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(shouldShowUnsaved)
    }
  }, [
    formAnswers,
    initialAnswers,
    submissionComplete,
    isConverted,
    onUnsavedChangesChange,
    isSubmitting, // ✅ important
  ])

  // Validation function for form fields
  const validateField = (questionId, value, questionType, isRequired) => {
    if (!isRequired || isConverted) return null

    if (questionType === "Multiple Selection" || questionType === "Checkbox") {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return "Please select at least one option"
      }
    } else if (questionType === "Multiple Choice" || questionType === "Single Selection") {
      if (!value || value.trim() === "") {
        return "Please select an option"
      }
    } else if (questionType === "Short Answer") {
      if (!value || value.trim() === "") {
        return "This field is required"
      }
    } else if (questionType === "Long Answer") {
      if (!value || value.trim() === "") {
        return "This field is required"
      }
    } else if (questionType === "Email") {
      if (!value || value.trim() === "") {
        return "Email is required"
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        return "Please enter a valid email"
      }
    } else if (questionType === "Phone") {
      if (!value || value.trim() === "") {
        return "Phone number is required"
      }
      const phoneRegex = /^[0-9\-\+\(\)\s]+$/
      if (!phoneRegex.test(value)) {
        return "Please enter a valid phone number"
      }
    } else if (questionType === "File Input") {
      if (!value) {
        return "Please upload a file"
      }
    }

    return null
  }

  // Validate entire form
  const validateForm = () => {
    const errors = {}

    const validateQuestion = (question) => {
      const questionId = question.form_question_id
      const answer = formAnswers[questionId]
      const error = validateField(questionId, answer, question.question_type, question.required === "Yes" || question.is_required === "Yes")
      if (error) {
        errors[questionId] = error
      }

      // Validate option questions if they have answers
      if (question.options && question.options.length > 0) {
        question.options.forEach((option) => {
          if (option.option_questions && option.option_questions.length > 0) {
            const isSelected = Array.isArray(formAnswers[questionId])
              ? formAnswers[questionId].includes(option.option_name)
              : formAnswers[questionId] === option.option_name

            if (isSelected) {
              option.option_questions.forEach((optionQuestion, optionIdx) => {
                const uniqueOptionQuestionId = generateUniqueOptionQuestionId(
                  questionId,
                  option,
                  optionQuestion,
                  optionIdx
                )
                const optionAnswer = formAnswers[uniqueOptionQuestionId]
                const optionError = validateField(
                  uniqueOptionQuestionId,
                  optionAnswer,
                  optionQuestion.question_type || "Short Answer",
                  optionQuestion.required === "Yes" || optionQuestion.is_required === "Yes"
                )
                if (optionError) {
                  errors[uniqueOptionQuestionId] = optionError
                }
              })
            }
          }
        })
      }
    }

    if (fields && fields.length > 0) {
      fields.forEach((field) => {
        if (field.isGroup === "Yes" && field.questions) {
          field.questions.forEach((question) => {
            validateQuestion(question)
          })
        } else {
          validateQuestion(field)
        }
      })
    }

    return errors
  }

  // Handle input change for questions and nested questions
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target

    setFormAnswers((prev) => {
      const updated = { ...prev }

      if (type === "checkbox") {
        const currentArray = Array.isArray(updated[name]) ? updated[name] : []
        if (checked) {
          if (!currentArray.includes(value)) {
            updated[name] = [...currentArray, value]
          }
        } else {
          updated[name] = currentArray.filter((item) => item !== value)
        }
      } else if (type === "radio") {
        updated[name] = value
      } else {
        updated[name] = value
      }

      setHasUnsavedChanges(true)
      return updated
    })

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: null,
      }))
    }
  }

  // Render nested option questions
  const renderOptionQuestionInput = (optionQuestion, parentQuestionId, option, optionIdx) => {
    const uniqueOptionQuestionId = generateUniqueOptionQuestionId(
      parentQuestionId,
      option,
      optionQuestion,
      optionIdx
    )
    const currentValue = formAnswers[uniqueOptionQuestionId] || ""
    
    // Debug logging for rendering
    if (optionQuestion.question_type === "Multiple Selection") {
      console.log("[v0] Rendering Multiple Selection option question:")
      console.log("  - Unique ID:", uniqueOptionQuestionId)
      console.log("  - Current Value from State:", currentValue)
      console.log("  - Is Array:", Array.isArray(currentValue))
      console.log("  - All formAnswers keys:", Object.keys(formAnswers).filter(k => k.includes(parentQuestionId)))
    }

    switch (optionQuestion.question_type || "Short Answer") {
      case "Multiple Choice":
      case "Single Selection":
        return (
          <div>
            {optionQuestion.options?.map((opt) => (
              <div className="form-check" key={opt.option_id}>
                <input
                  type="radio"
                  className="form-check-input"
                  name={uniqueOptionQuestionId}
                  value={opt.option_name}
                  checked={valueMatchesOption(currentValue, opt.option_name)}
                  onChange={handleInputChange}
                  disabled={isConverted}
                  id={`${uniqueOptionQuestionId}_${opt.option_id}`}
                />
                <label className="form-check-label" htmlFor={`${uniqueOptionQuestionId}_${opt.option_id}`}>
                  {opt.option_name}
                </label>
              </div>
            ))}
            {formErrors[uniqueOptionQuestionId] && (
              <div className="text-danger small mt-1">{formErrors[uniqueOptionQuestionId]}</div>
            )}
          </div>
        )

      case "Multiple Selection":
      case "Checkbox":
        return (
          <div>
            {optionQuestion.options?.map((opt) => {
              const isChecked = Array.isArray(currentValue)
                ? currentValue.some(val => valueMatchesOption(val, opt.option_name))
                : valueMatchesOption(currentValue, opt.option_name)
              return (
                <div className="form-check" key={opt.option_id}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name={uniqueOptionQuestionId}
                    value={opt.option_name}
                    checked={isChecked}
                    onChange={handleInputChange}
                    disabled={isConverted}
                    id={`${uniqueOptionQuestionId}_${opt.option_id}`}
                  />
                  <label className="form-check-label" htmlFor={`${uniqueOptionQuestionId}_${opt.option_id}`}>
                    {opt.option_name}
                  </label>
                </div>
              )
            })}
            {formErrors[uniqueOptionQuestionId] && (
              <div className="text-danger small mt-1">{formErrors[uniqueOptionQuestionId]}</div>
            )}
          </div>
        )

      case "Long Answer":
        return (
          <div>
            <textarea
              className="form-control mt-2"
              value={currentValue}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: uniqueOptionQuestionId,
                    value: e.target.value,
                    type: "textarea",
                  },
                })
              }
              placeholder="Enter your detailed answer"
              disabled={isConverted}
              rows="4"
            ></textarea>
            {formErrors[uniqueOptionQuestionId] && (
              <div className="text-danger small mt-1">{formErrors[uniqueOptionQuestionId]}</div>
            )}
          </div>
        )

      case "Email":
        return (
          <div>
            <input
              className="form-control mt-2"
              type="email"
              value={currentValue}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: uniqueOptionQuestionId,
                    value: e.target.value,
                    type: "email",
                  },
                })
              }
              placeholder="Enter your email"
              disabled={isConverted}
            />
            {formErrors[uniqueOptionQuestionId] && (
              <div className="text-danger small mt-1">{formErrors[uniqueOptionQuestionId]}</div>
            )}
          </div>
        )

      case "Phone":
        return (
          <div>
            <input
              className="form-control mt-2"
              type="tel"
              value={currentValue}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: uniqueOptionQuestionId,
                    value: e.target.value,
                    type: "tel",
                  },
                })
              }
              placeholder="Enter your phone number"
              disabled={isConverted}
            />
            {formErrors[uniqueOptionQuestionId] && (
              <div className="text-danger small mt-1">{formErrors[uniqueOptionQuestionId]}</div>
            )}
          </div>
        )

      case "Date":
        return (
          <div>
            <input
              className="form-control mt-2"
              type="date"
              value={currentValue}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: uniqueOptionQuestionId,
                    value: e.target.value,
                    type: "date",
                  },
                })
              }
              disabled={isConverted}
            />
            {formErrors[uniqueOptionQuestionId] && (
              <div className="text-danger small mt-1">{formErrors[uniqueOptionQuestionId]}</div>
            )}
          </div>
        )

      case "Time":
        return (
          <div>
            <div className="input-group mt-2">
              <input
                className="form-control"
                type="time"
                value={currentValue}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: uniqueOptionQuestionId,
                      value: e.target.value,
                      type: "time",
                    },
                  })
                }
                disabled={isConverted}
              />
              <span className="input-group-text">
                <i className="far fa-clock"></i>
              </span>
            </div>
            {formErrors[uniqueOptionQuestionId] && (
              <div className="text-danger small mt-1">{formErrors[uniqueOptionQuestionId]}</div>
            )}
          </div>
        )

      case "File Input":
        return (
          <div>
            <input
              type="file"
              className="form-control mt-2"
              onChange={(e) => {
                const file = e.target.files[0]
                if (file) {
                  const reader = new FileReader()
                  reader.onload = () => {
                    setFormAnswers((prev) => ({
                      ...prev,
                      [uniqueOptionQuestionId]: {
                        file: file,
                        fileName: file.name,
                        fileType: file.type,
                        fileSize: file.size,
                      },
                    }))
                    setFilePreviews((prev) => ({
                      ...prev,
                      [uniqueOptionQuestionId]: reader.result,
                    }))
                  }
                  reader.readAsDataURL(file)
                }
              }}
              disabled={isConverted}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
            />
            {filePreviews[uniqueOptionQuestionId] && (
              <div className="mt-2">
                {filePreviews[uniqueOptionQuestionId].includes("image") ? (
                  <img src={filePreviews[uniqueOptionQuestionId]} alt="preview" style={{ maxWidth: "100px" }} />
                ) : (
                  <p>File: {formAnswers[uniqueOptionQuestionId]?.fileName}</p>
                )}
              </div>
            )}
            {formErrors[uniqueOptionQuestionId] && (
              <div className="text-danger small mt-1">{formErrors[uniqueOptionQuestionId]}</div>
            )}
          </div>
        )

      default:
        return (
          <div>
            <input
              className="form-control mt-2"
              type="text"
              value={currentValue}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: uniqueOptionQuestionId,
                    value: e.target.value,
                    type: "text",
                  },
                })
              }
              placeholder="Enter your answer"
              disabled={isConverted}
            />
            {formErrors[uniqueOptionQuestionId] && (
              <div className="text-danger small mt-1">{formErrors[uniqueOptionQuestionId]}</div>
            )}
          </div>
        )
    }
  }

  // Render individual question field
  const renderQuestionField = (field) => {
    const {
      form_question_id,
      label,
      question_type,
      options,
      required,
      is_required,
    } = field

    switch (question_type) {
      case "Multiple Choice":
      case "Single Selection":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <div>
              {options?.map((option) => {
                const hasOptionQuestions = option.option_questions && option.option_questions.length > 0
                return (
                  <div className="form-check" key={option.option_id}>
                    <input
                      type="radio"
                      className="form-check-input"
                      name={form_question_id}
                      value={option.option_name}
                      checked={valueMatchesOption(formAnswers[form_question_id], option.option_name)}
                      onChange={handleInputChange}
                      disabled={isConverted}
                    />
                    <label className="form-check-label">
                      {option.option_name}
                      {hasOptionQuestions && (
                        <span className="badge bg-info ms-2" title={`${option.option_questions.length} sub-questions`}>
                          {option.option_questions.length}
                        </span>
                      )}
                    </label>
                    {valueMatchesOption(formAnswers[form_question_id], option.option_name) && hasOptionQuestions && (
                      <div className="ms-4 mt-2 mb-3 p-3 bg-light rounded" style={{ borderLeft: "3px solid #007bff" }}>
                        {option.option_questions.map((optionQuestion, optionIdx) => {
                          const uniqueOptionQuestionId = generateUniqueOptionQuestionId(
                            form_question_id,
                            option,
                            optionQuestion,
                            optionIdx
                          );
                          return (
                            <div key={uniqueOptionQuestionId} className="mb-3">
                              <label className="form-label">
                                <strong>{optionQuestion.label}</strong>
                                {optionQuestion.is_required === "Yes" && <span className="text-danger"> *</span>}
                              </label>
                              {formErrors[uniqueOptionQuestionId] && (
                                <div className="text-danger small">{formErrors[uniqueOptionQuestionId]}</div>
                              )}
                              {renderOptionQuestionInput(optionQuestion, form_question_id, option, optionIdx)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Multiple Selection":
      case "Checkbox":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <div>
              {options?.map((option) => {
                const hasOptionQuestions = option.option_questions && option.option_questions.length > 0
                const selectedValue = formAnswers[form_question_id];
const optionNameTrimmed = (option.option_name || "").trim();
const isSelected = Array.isArray(selectedValue)
  ? selectedValue.map(v => (v || "").trim()).includes(optionNameTrimmed)
  : (selectedValue || "").trim() === optionNameTrimmed;

                return (
                  <div className="form-check" key={option.option_id}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name={form_question_id}
                      value={option.option_name}
                      checked={isSelected}
                      onChange={handleInputChange}
                      disabled={isConverted}
                    />
                    <label className="form-check-label">
                      {option.option_name}
                      {hasOptionQuestions && (
                        <span className="badge bg-info ms-2" title={`${option.option_questions.length} sub-questions`}>
                          {option.option_questions.length}
                        </span>
                      )}
                    </label>
                    {isSelected && hasOptionQuestions && (
                      <div className="ms-4 mt-2 mb-3 p-3 bg-light rounded" style={{ borderLeft: "3px solid #007bff" }}>
                        {option.option_questions.map((optionQuestion, optionIdx) => {
                          const uniqueOptionQuestionId = generateUniqueOptionQuestionId(
                            form_question_id,
                            option,
                            optionQuestion,
                            optionIdx
                          );
                          return (
                            <div key={uniqueOptionQuestionId} className="mb-3">
                              <label className="form-label">
                                <strong>{optionQuestion.label}</strong>
                                {optionQuestion.is_required === "Yes" && <span className="text-danger"> *</span>}
                              </label>
                              {formErrors[uniqueOptionQuestionId] && (
                                <div className="text-danger small">{formErrors[uniqueOptionQuestionId]}</div>
                              )}
                              {renderOptionQuestionInput(optionQuestion, form_question_id, option, optionIdx)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Short Answer":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <input
              className="form-control"
              type="text"
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              name={form_question_id}
              placeholder="Enter your answer"
              disabled={isConverted}
            />
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Long Answer":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <textarea
              className="form-control"
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              name={form_question_id}
              placeholder="Enter your detailed answer"
              disabled={isConverted}
              rows="4"
            ></textarea>
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Email":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <input
              className="form-control"
              type="email"
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              name={form_question_id}
              placeholder="Enter your email"
              disabled={isConverted}
            />
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Phone":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <input
              className="form-control"
              type="tel"
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              name={form_question_id}
              placeholder="Enter your phone number"
              disabled={isConverted}
            />
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Date":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <input
              className="form-control"
              type="date"
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              name={form_question_id}
              disabled={isConverted}
            />
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Time":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <div className="input-group">
              <input
                className="form-control"
                type="time"
                value={formAnswers[form_question_id] || ""}
                onChange={handleInputChange}
                name={form_question_id}
                disabled={isConverted}
              />
              <span className="input-group-text">
                <i className="far fa-clock"></i>
              </span>
            </div>
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "File Input":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <input
              type="file"
              className="form-control"
              onChange={(e) => {
                const file = e.target.files[0]
                if (file) {
                  const reader = new FileReader()
                  reader.onload = () => {
                    setFormAnswers((prev) => ({
                      ...prev,
                      [form_question_id]: {
                        file: file,
                        fileName: file.name,
                        fileType: file.type,
                        fileSize: file.size,
                      },
                    }))
                    setFilePreviews((prev) => ({
                      ...prev,
                      [form_question_id]: reader.result,
                    }))
                  }
                  reader.readAsDataURL(file)
                }
              }}
              disabled={isConverted}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
            />
            {filePreviews[form_question_id] && (
              <div className="mt-2">
                {filePreviews[form_question_id].includes("image") ? (
                  <img src={filePreviews[form_question_id]} alt="preview" style={{ maxWidth: "100px" }} />
                ) : (
                  <p>File: {formAnswers[form_question_id]?.fileName}</p>
                )}
              </div>
            )}
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      default:
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <input
              className="form-control"
              type="text"
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              name={form_question_id}
              placeholder="Enter your answer"
              disabled={isConverted}
            />
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )
    }
  }

  // Render question group
  const renderQuestionGroup = (field) => {
    const { label, questions } = field
    const nestedSubquestionIds = getNestedOptionQuestionIds(questions || [])

    return (
      <div key={field.group_id} className="col-12 mb-3">
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">{label}</h5>
          </div>
          <div className="card-body">
            <div className="row">
              {questions?.map((question) => {
                if (nestedSubquestionIds.has(question.form_question_id)) {
                  return null
                }
                return renderQuestionField(question)
              })}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Enhanced handleSubmit with proper nested question payload
  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
  
    try {
      const errors = validateForm()
  
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors)
        return
      }
  
      const answersArray = []
      let sequenceOrder = 1
  
      // Process all main questions and nested questions
      for (const field of fields || []) {
        let questionsToProcess = []
  
        if (field.isGroup === "Yes" && field.questions) {
          questionsToProcess = field.questions.map((q) => ({
            ...q,
            group_id: field.group_id,
          }))
        } else {
          questionsToProcess = [
            {
              ...field,
              group_id: field.group_id || 0,
            },
          ]
        }
  
        // Process each main question
        for (const question of questionsToProcess) {
          const questionId = question.form_question_id
          const answer = formAnswers[questionId]
  
          // Add main question answer
          if (answer) {
            let finalAnswer = null
            let fileMeta = {}
  
            if (question.question_type === "File Input") {
              if (answer?.file) {
                const reader = new FileReader()
  
                const fileBase64 = await new Promise((resolve, reject) => {
                  reader.onloadend = () => resolve(reader.result)
                  reader.onerror = reject
                  reader.readAsDataURL(answer.file)
                })
  
                fileMeta = {
                  file_name: answer.file.name,
                  file_size: answer.file.size,
                  file_type: answer.file.type,
                }
  
                finalAnswer = fileBase64
              } else if (answer?.fileBase64) {
                finalAnswer = answer.fileBase64
  
                fileMeta = {
                  file_name: answer.fileName || `file_${questionId}`,
                  file_size: answer.fileSize || 0,
                  file_type:
                    answer.fileType || "application/octet-stream",
                }
              }
            } else {
              finalAnswer = Array.isArray(answer)
                ? answer.join(", ")
                : answer
            }
  
            if (finalAnswer) {
              let optionId = null
  
              if (question.options && question.options.length > 0) {
                const selectedOption = question.options.find(
                  (opt) =>
                    opt.option_name === finalAnswer ||
                    opt.label === finalAnswer
                )
  
                optionId = selectedOption?.option_id || null
              }
  
              answersArray.push({
                question_id: question.question_id,
                form_question_id: questionId,
                answer: finalAnswer,
                question: question.question || question.label,
                option_id: optionId,
                group_id: question.group_id || 0,
                question_type: question.question_type,
                sequence_order: sequenceOrder++,
                ...fileMeta,
              })
            }
          }
  
          // Nested questions
          if (question.options && question.options.length > 0) {
            for (const option of question.options) {
              if (
                option.option_questions &&
                option.option_questions.length > 0
              ) {
                const isSelected = Array.isArray(
                  formAnswers[questionId]
                )
                  ? formAnswers[questionId].includes(
                      option.option_name
                    )
                  : formAnswers[questionId] === option.option_name
  
                if (isSelected) {
                  for (const optionQuestion of option.option_questions) {
                    const optionIdx =
                      option.option_questions.indexOf(optionQuestion)
  
                    const uniqueOptionQuestionId =
                      generateUniqueOptionQuestionId(
                        questionId,
                        option,
                        optionQuestion,
                        optionIdx
                      )
  
                    const optionAnswer =
                      formAnswers[uniqueOptionQuestionId]
  
                    if (optionAnswer) {
                      let finalOptionAnswer = null
                      let optionFileMeta = {}
  
                      if (
                        optionQuestion.question_type ===
                        "File Input"
                      ) {
                        if (optionAnswer?.file) {
                          const reader = new FileReader()
  
                          const fileBase64 = await new Promise(
                            (resolve, reject) => {
                              reader.onloadend = () =>
                                resolve(reader.result)
                              reader.onerror = reject
                              reader.readAsDataURL(optionAnswer.file)
                            }
                          )
  
                          optionFileMeta = {
                            file_name: optionAnswer.file.name,
                            file_size: optionAnswer.file.size,
                            file_type: optionAnswer.file.type,
                          }
  
                          finalOptionAnswer = fileBase64
                        } else if (optionAnswer?.fileBase64) {
                          finalOptionAnswer =
                            optionAnswer.fileBase64
  
                          optionFileMeta = {
                            file_name:
                              optionAnswer.fileName ||
                              `file_${uniqueOptionQuestionId}`,
                            file_size:
                              optionAnswer.fileSize || 0,
                            file_type:
                              optionAnswer.fileType ||
                              "application/octet-stream",
                          }
                        }
                      } else {
                        finalOptionAnswer = Array.isArray(
                          optionAnswer
                        )
                          ? optionAnswer.join(", ")
                          : optionAnswer
                      }
  
                      if (finalOptionAnswer) {
                        answersArray.push({
                          question_id:
                            optionQuestion.id ||
                            optionQuestion.question_id,
                          form_question_id:
                            optionQuestion.form_question_id,
                          answer: finalOptionAnswer,
                          question:
                            optionQuestion.label ||
                            optionQuestion.question,
                          parent_question_id: questionId,
                          parent_option_id: option.option_id,
                          group_id: question.group_id || 0,
                          question_type:
                            optionQuestion.question_type ||
                            "Short Answer",
                          sequence_order: sequenceOrder++,
                          ...optionFileMeta,
                        })
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
  
      console.log(
        "[v0] Final answersArray (questions + subquestions):",
        answersArray
      )
  
      const result = await dispatch(
        submitQuestionnaire(leadId, answersArray)
      )
  
      console.log("[v0] API submission result:", result)
  
      if (result.status === "success") {
        setInitialAnswers({ ...formAnswers })
        setIsSubmitted(true)
        setSubmissionComplete(true)
        setHasUnsavedChanges(false)
  
        Object.values(filePreviews).forEach((preview) => {
          if (preview) URL.revokeObjectURL(preview)
        })
  
        dispatch(fetchQuestionnaire(leadId))
  
        if (onUnsavedChangesChange) {
          onUnsavedChangesChange(false)
        }
  
        onSubmit(formAnswers)
  
        console.log(
          "[v0] Questionnaire submitted successfully"
        )
      }
    } catch (error) {
      console.error("[v0] Failed to submit form:", error)
    } finally {
      // ✅ prevents modal flash after submit
      setIsSubmitting(false)
    }
  }

  // Clean up file previews on component unmount
  React.useEffect(() => {
    return () => {
      Object.values(filePreviews).forEach(preview => {
        if (preview) URL.revokeObjectURL(preview)
      })
    }
  }, [filePreviews])

  if (loading) {
    return (
      <div className="card shadow-none">
        <div className="card-body text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card shadow-none">
        <div className="card-body text-center text-danger">
          <p>Error loading questionnaire: {error}</p>
          <button
            className="btn btn-primary"
            onClick={() => dispatch(fetchQuestionnaire(leadId))}
            disabled={isConverted}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="card shadow-none">
        <div className="card-body">
          {isConverted && (
            <div className="alert alert-info mb-3">
              This lead has been converted. The questionnaire is in view-only mode.
            </div>
          )}
          <div style={{ maxHeight: "80vh", overflowY: "auto", paddingRight: "15px" }}>
            {!fields || fields.length === 0 ? (
              <div className="alert alert-info text-center">No questionnaire is available for this lead.</div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {fields?.map((field, index) => {
                    let renderedField
                    if (field.isGroup === "Yes") {
                      renderedField = renderQuestionGroup(field)
                    } else {
                      renderedField = renderQuestionField(field)
                    }
                    return renderedField
                  })}
                </div>
                {!isConverted && (
                  <div className="text-center mt-3">
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit Questionnaire"
                      )}
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

QuestionnaireComponent.propTypes = {
  leadId: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isConverted: PropTypes.bool,
  onUnsavedChangesChange: PropTypes.func,
}

QuestionnaireComponent.defaultProps = {
  isConverted: false,
}

export default QuestionnaireComponent
